<template>
    <x-container fluid app-width>
        <x-layout>
            <x-flex>
                <template v-if="loading">
                    <loading-spinner />
                </template>

                <x-container v-else fluid class="summary-container">
                    <x-layout>
                        <x-flex xs12>
                            <summary-tabs-buttons
                                v-model="activeTab"
                                :tabs="summaryTabs"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="Boolean(pendingApprovedEndorsement)" mb-4>
                        <x-flex>
                            <view-details-banner
                                :text="pendingEndorsementText"
                                :scroll-to-id="pendingApprovedEndorsement.type"
                                @clicked="viewDetails(pendingApprovedEndorsement)"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout v-if="Boolean(inProgressEndorsement)" mb-4>
                        <x-flex>
                            <view-details-banner
                                :text="inProgressEndorsementText"
                                :scroll-to-id="inProgressEndorsement.type"
                                subject="amendment"
                                @clicked="viewDetails(inProgressEndorsement)"
                            />
                        </x-flex>
                    </x-layout>

                    <template v-if="activeTab === 'product'">
                        <x-layout mb-5>
                            <x-flex xs6 class="left">
                                <x-card class="panel">
                                    <x-card-title>
                                        <h3>{{ declarationDetailsHeading() }}</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <standalone-policy-summary-data-list v-if="isStandalonePolicy" v-model="declaration" />

                                        <summary-data-list v-else v-model="declaration" />

                                        <div v-if="showEditUmr" class="summary-field-update-row mt-4">
                                            <x-text-field
                                                v-model="umr"
                                                label="Declaration UMR"
                                                placeholder=" "
                                                :rules="[v => ruleUmr(v, '', 'UMR format not valid')]"
                                                @change="umr = umr.toUpperCase()"
                                                outline
                                            />

                                            <x-btn
                                                class="ml-3"
                                                color="secondary"
                                                small
                                                :disabled="!umr || ruleUmr(umr, '', 'UMR format not valid') !== true"
                                                @click="addUMR"
                                            >
                                                Save
                                            </x-btn>
                                        </div>

                                        <div v-if="canChangeClientReference" class="summary-field-update-row mt-4">
                                            <x-text-field
                                                v-model="declaration.client_reference"
                                                label="Client reference"
                                                placeholder=" "
                                                outline
                                            />

                                            <x-btn
                                                class="ml-3"
                                                color="secondary"
                                                small
                                                @click="addUpdateClientReference"
                                            >
                                                Save
                                            </x-btn>
                                        </div>
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="isCaptiveReinsurance" class="panel mt-5">
                                    <x-card-title>
                                        <h3>Reinsurer declaration details</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <summary-data-list v-model="declaration" :is-reinsurer="true" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>

                            <x-flex xs6 class="right">
                                <x-card class="panel mb-5">
                                    <x-card-title>
                                        <h3>Signed lines</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <signed-lines-table
                                            table-class="inverse"
                                            :lines="declaration.lines"
                                            :facility="declaration.facility"
                                            :currency-code="declaration.currency.code"
                                            :show-final-premium="hasFinalPremium"
                                            :isStandalonePolicy="isStandalonePolicy"
                                        />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor" class="panel mb-5">
                                    <x-card-title>
                                        <h3>Obligors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="declaration.obligors" obligor :currency-code="declaration.currency.code" />
                                    </x-card-text>
                                </x-card>

                                <x-card v-if="hasObligor && declaration.guarantors.length > 0" class="panel">
                                    <x-card-title>
                                        <h3>Guarantors</h3>
                                    </x-card-title>

                                    <x-card-text>
                                        <obligor-data-list v-model="declaration.guarantors" />
                                    </x-card-text>
                                </x-card>
                            </x-flex>
                        </x-layout>

                        <x-layout id="repayment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Repayments</h3>

                                <repayments-table
                                    :repayments="repayments"
                                    :declaration="declaration"
                                    :cancelAction="true"
                                />

                                <div v-if="canMakeRepayment && !isOffRisk" class="mt-4 text-xs-right">
                                    <x-btn
                                        color="secondary"
                                        :disabled="hasActiveEndorsements"
                                        @click="openRepayment"
                                    >
                                        Make repayment
                                    </x-btn>
                                </div>
                            </x-flex>
                        </x-layout>

                        <x-layout id="mid-term-adjustment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Mid-term adjustment</h3>

                                <mid-term-adjustment-table
                                    :mid-term-adjustment="midTermAdjustment"
                                    :declaration="declaration"
                                    :insurer-list="insurerList"
                                    :mta-loading="mtaLoading"
                                    :user-type="$root.userIsBrokerUser() ? 'broker' : 'client'"
                                    @updateDeclarationData="updateDeclarationData"
                                    @loadMidTermAdjustment="loadMidTermAdjustment"
                                />

                                <div v-if="$root.userIsBrokerUser()" class="mt-4 text-xs-right">
                                    <x-btn
                                        color="secondary"
                                        :disabled="hasActiveEndorsements"
                                        @click="createMidTermAdjustment()"
                                    >
                                        Create an MTA
                                    </x-btn>
                                </div>
                            </x-flex>
                        </x-layout>

                        <x-layout id="amendment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Amendments</h3>

                                <amendments-table
                                    :endorsements="endorsements"
                                    :declaration="declaration"
                                    :insurer-list="insurerList"
                                    @cancelAmendment="cancelAmendment"
                                    @updateInsurerApprovals="updateInsurerApprovals"
                                />

                                <div v-if="!isOffRisk && !isCommitment && !isInvoice" class="mt-4 text-xs-right">
                                    <x-btn
                                        color="secondary"
                                        :disabled="hasActiveEndorsements"
                                        @click="createAmendment"
                                    >
                                        Create amendment
                                    </x-btn>
                                </div>
                            </x-flex>
                        </x-layout>

                        <x-layout v-if="isCommitment" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Invoices</h3>

                                <invoices-table :invoices="declaration.invoices" />

                                <div v-if="$root.userIsBrokerUser()" class="mt-4 text-xs-right">
                                    <x-btn
                                        color="secondary"
                                        :disabled="!canCreateInvoice"
                                        @click="createInvoice"
                                    >
                                        Create invoice
                                    </x-btn>
                                </div>
                            </x-flex>
                        </x-layout>

                        <x-layout v-if="$root.userIsBrokerUser()" mb-5>
                            <x-flex>
                                <h3 class="pb-3">Subscription agreements</h3>

                                <subscription-agreements-table :declaration="declaration" />

                                <div class="mt-4 text-xs-right">
                                    <x-btn
                                        color="secondary"
                                        @click="createSubscriptionAgreement"
                                    >
                                        Create subscription agreement
                                    </x-btn>
                                </div>
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex>
                                <h3 class="pb-3">Version history</h3>

                                <version-history-table
                                    :risk="declaration"
                                    :audits="versionHistoryRows"
                                    :product-config-key="declaration.product_config_key"
                                    userType="broker"
                                    :currency-code="declaration.currency.code"
                                />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'additional_information'">
                        <x-layout>
                            <x-flex>
                                <additional-information-tab
                                    v-model="declaration.information_sections"
                                    :insurer-list="insurerList"
                                />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'market_response_sheet'">
                        <x-layout>
                            <x-flex>
                                <market-response-sheet-tab :risk="declaration" />
                            </x-flex>
                        </x-layout>
                    </template>

                    <template v-if="activeTab === 'documents'">
                        <document-list
                            :insurer-list="insurerList"
                            insurer-specific-upload
                            upload
                            :facility-id="declaration.facility_id"
                            :documentable-id="declaration.id"
                            documentable-type="declaration"
                            :can-delete="({documentable_type}) => documentable_type === 'declaration'"
                        />
                    </template>
                </x-container>
            </x-flex>
        </x-layout>

        <modal
            ref="inProgressAmendmentConfirmation"
            title="Create amendment"
            confirmation
            action-text="Yes"
        >
            <p>Creating a new amendment will Cancel the amendment that is currently In progress.  Are you sure?</p>
        </modal>

        <x-dialog
            v-if="showOffRiskDeclarationDialog"
            v-model="showOffRiskDeclarationDialog"
            width="900"
            max-width="900"
            content-class="overflow-scroll"
        >
            <x-form ref="offRiskForm" v-model="offRiskValid">
                <x-container fluid>
                    <x-layout>
                        <x-flex xs12>
                            <h2>Off risk declaration</h2>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12>
                            <div v-for="line in declaration.lines" :key="line.id">
                                <x-currency-text-field
                                    v-model="line.final_premium"
                                    :label="`Final premium for ${getLineInsurerName(line)}`"
                                    placeholder=" "
                                    :prefix="declaration.currency.code"
                                    :rules="[
                                        () => ruleRequired(line.final_premium, 'Final premium'),
                                    ]"
                                    outline
                                    :disabled="line.offer_type === 'flat_fee'"
                                />
                            </div>

                            <x-menu
                                v-model="offRiskDateInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="offRiskDate"
                                        label="Off risk date"
                                        placeholder=" "
                                        append-icon="event"
                                        :rules="[v => ruleRequired(v, 'Off risk date')]"
                                        outline
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="offRiskDate"
                                    @input="offRiskDateInput = false"
                                    :min="$moment(declaration.inception_date).format('YYYY-MM-DD')"
                                />
                            </x-menu>

                            <x-menu
                                v-model="settlementDueDateInput"
                                :close-on-content-click="false"
                                lazy
                                transition="scale-transition"
                                offset-y
                                max-width="300"
                                full-width
                            >
                                <template v-slot:activator="{ on }">
                                    <x-text-field
                                        v-model="settlementDueDate"
                                        label="Settlement due date"
                                        placeholder=" "
                                        append-icon="event"
                                        :rules="[v => ruleRequired(v, 'Settlement due date')]"
                                        outline
                                        readonly
                                        v-on="on"
                                    />
                                </template>

                                <x-date-picker
                                    v-model="settlementDueDate"
                                    @input="settlementDueDateInput = false"
                                    :min="offRiskDate || $moment(declaration.inception_date).format('YYYY-MM-DD')"
                                />
                            </x-menu>
                        </x-flex>
                    </x-layout>

                    <x-layout>
                        <x-flex xs12 text-xs-right>
                            <document-list
                                :insurer-list="insurerList"
                                :facility-id="declaration.facility_id"
                                insurer-specific-upload
                                upload
                                :documentable-id="declaration.id"
                                documentable-type="declaration"
                                :documentable-category-id="2"
                                column
                                :can-delete="({documentable_type}) => documentable_type === 'declaration'"
                            />
                        </x-flex>
                    </x-layout>

                    <x-layout class="response-actions">
                        <x-flex class="text-xs-right">
                            <x-btn
                                @click="offRiskDeclaration"
                                class="ma-0"
                                color="primary"
                                small
                                :disabled="!offRiskValid"
                                :loading="offRiskLoading"
                            >
                                Submit

                                <x-icon class="ml-2">trending_flat</x-icon>
                            </x-btn>
                        </x-flex>
                    </x-layout>
                </x-container>
            </x-form>
        </x-dialog>
    </x-container>
</template>

<script>
    import SummaryTabsButtons from '~/components/Shared/UIComponents/SummaryTabsButtons';
    import SummaryDataList from '~/components/Shared/UIComponents/SummaryDataList';
    import AdditionalInformationTab from '~/components/Shared/UIComponents/AdditionalInformationTab';
    import SignedLinesTable from '~/components/Broker/UIComponents/SignedLinesTable';
    import RepaymentsTable from '~/components/Shared/UIComponents/RepaymentsTable';
    import MidTermAdjustmentTable from '~/components/Shared/UIComponents/MidTermAdjustmentTable';
    import AmendmentsTable from '~/components/Broker/UIComponents/AmendmentsTable';
    import InvoicesTable from '~/components/Shared/UIComponents/InvoicesTable';
    import SubscriptionAgreementsTable from '~/components/Broker/UIComponents/SubscriptionAgreementsTable';
    import VersionHistoryTable from '~/components/Shared/UIComponents/VersionHistoryTable';
    import DocumentList from '~/components/Shared/UIComponents/DocumentList';
    import DataList from '~/components/Shared/UIComponents/DataList';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import rules from '~/mixins/rules';
    import ObligorDataList from "~/components/Shared/UIComponents/ObligorDataList";
    import ViewDetailsBanner from "../../../components/Shared/UIComponents/ViewDetailsBanner.vue";
    import StandalonePolicySummaryDataList from '../../../components/Shared/UIComponents/StandalonePolicySummaryDataList.vue';
    import MarketResponseSheetTab from "../../../components/Broker/UIComponents/MarketResponseSheetTab.vue";

    export default {
        name: 'BrokerDeclarationSummary',
        components: {
            SummaryTabsButtons,
            MarketResponseSheetTab,
            ViewDetailsBanner,
            SummaryDataList,
            AdditionalInformationTab,
            SignedLinesTable,
            RepaymentsTable,
            MidTermAdjustmentTable,
            AmendmentsTable,
            InvoicesTable,
            SubscriptionAgreementsTable,
            VersionHistoryTable,
            DocumentList,
            DataList,
            Modal,
            ObligorDataList,
            StandalonePolicySummaryDataList,
        },
        mixins: [rules],
        created() {
            this.fetchData();

            this.$eventBus.$on('action-download-mrc', () => this.download('mrc'));
            this.$eventBus.$on('action-download-summary', () => this.download('declaration'));
            this.$eventBus.$on('action-download-policy-wording', () => this.download('policy-wording'));
            this.$eventBus.$on('action-download-captive-summary', () => this.download('captive-declaration'));
            this.$eventBus.$on('action-download-reinsurer-summary', () => this.download('reinsurer-declaration'));

            this.$eventBus.$on('action-make-repayment', () => { this.openRepayment() });
            this.$eventBus.$on('action-make-mid-term-adjustment', () => { this.createMidTermAdjustment() });
            this.$eventBus.$on('action-close-declaration', () => { this.showOffRiskModal() });
            this.$eventBus.$on('action-create-amendment', () => { this.createAmendment() });
            this.$eventBus.$on('update-repayments', () => { this.updateRepayments() });
            this.$eventBus.$on('update-data', () => { this.fetchData() });
            this.$eventBus.$on('open-dialog', () => { this.showRepaymentDialog = true });
        },
        beforeDestroy() {
            this.$eventBus.$off('action-download-mrc');
            this.$eventBus.$off('action-download-summary');
            this.$eventBus.$off('action-download-policy-wording');
            this.$eventBus.$off('action-download-captive-summary');
            this.$eventBus.$off('action-download-reinsurer-summary');
            this.$eventBus.$off('action-make-repayment');
            this.$eventBus.$off('action-close-declaration');
            this.$eventBus.$off('action-create-amendment');
        },
        methods: {
            declarationDetailsHeading() {
                if (this.isStandalonePolicy) {
                    return 'Policy details';
                }
                return this.isCaptiveReinsurance ? 'Original declaration details' : 'Declaration details';
            },
            showOffRiskModal() {
                this.declaration.lines.forEach((line) => {
                    line.offer_type === 'flat_fee' ? line.final_premium = line.flat_fee : line.final_premium = '';
                });

                this.showOffRiskDeclarationDialog = true;
            },
            async fetchData() {
                this.loading = true;

                const dataRequests = [];

                dataRequests.push(axios.get(this.$root.api(`broker/declaration/${this.$route.params.id}`)).then(res => {
                    this.declaration = res.data.data;
                    this.umr = this.declaration.umr;
                }));

                await axios.all(dataRequests).then(() => {
                    this.mtaLoading = true;

                    axios.get(this.$root.api(`repayments/${this.declaration.id}`)).then(res => {
                        this.repayments = res.data.data;
                    });

                    this.loadMidTermAdjustment();

                    axios.get(this.$root.api(`audit/declarations/${this.declaration.id}`)).then(res => {
                        this.declarationAudit = res.data.data;
                    });
                    this.loading = false;
                    this.showRepaymentDialog = false;
                });
            },
            loadMidTermAdjustment() {
                this.mtaLoading = true;
                axios.get(this.$root.api(`mid-term-adjustment/${this.declaration.id}`)).then(res => {
                    this.midTermAdjustment = res.data.data;
                    this.mtaLoading = false;
                });
            },
            async download(type) {
                let url = null;
                switch (type) {
                    case 'mrc':
                        url = `/api/declaration/${this.$route.params.id}/documents/mrc-download/${this.declaration.id}`;
                        break;
                    case 'declaration':
                        url = `/api/declaration/${this.$route.params.id}/documents/download/${this.declaration.id}`;
                        break;
                    case 'policy-wording':
                        url = `/api/declaration/${this.$route.params.id}/documents/policy-wording-download/${this.declaration.id}`;
                        break;
                    case 'captive-declaration':
                        url = `/api/declaration/${this.$route.params.id}/documents/captive-download/${this.declaration.id}`;
                        break;
                    case 'reinsurer-declaration':
                        url = `/api/declaration/${this.$route.params.id}/documents/reinsurer-download/${this.declaration.id}`;
                        break;
                }

                if (url !== null || typeof url !== "undefined") {
                    window.location.href = (await axios.get(url)).data.url;
                }
                return false;
            },
            async offRiskDeclaration() {
                this.offRiskLoading = true;
                let offRiskData = {
                    lines: this.declaration.lines,
                    effective_date: this.offRiskDate,
                    settlement_due_date: this.settlementDueDate
                };

                await axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/off-risk`), offRiskData).then(() => {
                    this.loading = true;
                    this.showOffRiskDeclarationDialog = false;
                    this.fetchData();
                }).catch(error => {
                    this.loading = false;
                    this.offRiskLoading = false;
                }).then(() => {
                    this.loading = false;
                    this.offRiskLoading = false;
                });
            },
            async addUMR() {
                this.loading = true;
                await axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/umr`), {
                    umr: this.umr
                }).catch(error => {
                    this.loading = false;
                }).then(() => {
                    this.loading = false;
                    this.fetchData();
                    this.editUMR = false;
                });
            },
            async addUpdateClientReference() {
                this.loading = true;
                await axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/client-reference`), {
                    client_reference: this.declaration.client_reference
                }).catch(error => {
                    this.loading = false;
                }).then(() => {
                    this.loading = false;
                    this.fetchData();
                });
            },
            async createAmendment() {
                if (this.isStandalonePolicy && this.inProgressEndorsement) {
                    if (await this.$refs.inProgressAmendmentConfirmation.open() === false) {
                        return;
                    }

                    this.cancelAmendment(this.inProgressEndorsement);
                }

                this.goToAmendment();
            },
            goToAmendment() {
                this.$router.push({path: `/agent/amendment-entry/${this.$route.params.id}`});
            },
            createSubscriptionAgreement() {
                this.$router.push({path: `/agent/risk/${this.$route.params.id}/subscription-agreements`});
            },
            createMidTermAdjustment() {
                this.$router.push({path: `/agent/risk/${this.$route.params.id}/mid-term-adjustment`});
            },
            tooltipText(type) {
                if (this.isOffRisk) {
                    return 'Declaration is off risk.';
                }
                if (this.hasActiveOffRiskRequest) {
                    return 'Cannot make ' + type + ' with a pending off risk declaration request.';
                }
                if (this.hasActiveRepayment) {
                    return 'Cannot make ' + type + ' with a pending repayment.';
                }
                if (this.hasActiveAmendment) {
                    return 'Cannot make ' + type + ' with a pending amendment.';
                }
                if (this.hasActiveMidTermAdjustment) {
                    return 'Cannot make ' + type + ' with a pending premium.';
                }
                return 'Cannot make changes.'
            },
            viewDetails(endorsement) {
                if (endorsement.type === 'amendment') {
                    this.openAmendmentPanelIndex = 0;
                }
                if (endorsement.type === 'repayment') {
                    this.openRepaymentPanelIndex = 0;
                }
            },
            updateDeclarationData() {
                this.fetchData();
                this.openPremiumPanelIndex = 0;
            },
            getLineInsurerName(line) {
                const insurerName = line.captive_insurer_company_id
                    ? line.firm_order_offer.captive_insurer_company.name
                    : line.firm_order_offer.insurer_company.name;

                return line.security ? `${insurerName} — ${line.security.name}` : insurerName;
            },
            getIndemnityLabel() {
                return this.isCommitment || this.isInvoice ? 'Maximum indemnity' : 'Indemnity';
            },
            createInvoice() {
                if (this.isCommitment) {
                    this.$router.push(
                        {
                            name: 'broker-new-risk',
                            params: {
                                id: 'new',
                                commitment: this.declaration
                            }
                        }
                    );
                }
            },
            async cancelAmendment(amendment) {
                await axios.put(this.$root.api(this.cancelAmendmentUrl(amendment)));

                this.$eventBus.$emit('update-data');
            },
            async updateInsurerApprovals(amendment, approvingInsurerIds) {
                this.loading = true;

                await axios.put(this.$root.api(`broker/declaration/${this.declaration.id}/amendment/${amendment.id}/approvals`), {
                    'approving_insurer_ids': approvingInsurerIds,
                });
                this.$eventBus.$emit('update-data');

                this.loading = false;
            },
            goToDashboard() {
                this.$router.push({name: 'broker-dashboard'});
            },
            cancelAmendmentUrl(amendment) {
                let cancelPendingUrl =  `broker/declaration/${this.declaration.risk_id}/amendment/${amendment.id}/cancel`;
                let cancelDraftUrl = `broker/declaration/${this.declaration.id}/amendment/${amendment.id}/cancel-draft`

                return this.isAmendmentDraft(amendment) ? cancelDraftUrl : cancelPendingUrl;
            },
            isAmendmentDraft(amendment) {
                return amendment.status === 'draft';
            },
            openRepayment() {
                axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/repayment/create-draft`)).then(response => {
                    this.repayment = response.data.data;
                    this.$eventBus.$emit('open-dialog', this.repayment);
                    this.showRepaymentDialog = true;
                });
            },
            updateRepayments() {
                this.loading = true;

                axios.get(this.$root.api(`repayments/${this.declaration.id}`)).then(res => {
                    this.repayments = res.data.data;
                    this.loading = false;
                });
            }
        },
        computed: {
            summaryTabs() {
                const tabs = [
                    {
                        value: 'product',
                        text: 'Product',
                    },
                ];

                if (this.declaration.information_sections.length > 0) {
                    tabs.push({
                        value: 'additional_information',
                        text: 'Additional Information',
                    })
                }

                tabs.push(
                    {
                        value: 'market_response_sheet',
                        text: 'Market Response Sheet',
                    },
                    {
                        value: 'documents',
                        text: 'Documents',
                    },
                );

                return tabs;
            },
            isStandalonePolicy() {
                return this.declaration.risk_structure === 'standalone_policy';
            },
            pendingEndorsementText() {
                return `An ${this.pendingApprovedEndorsement.type} is ${this.pendingApprovedEndorsement.status === 'captive_approved' ? 'pending' : this.pendingApprovedEndorsement.status} and will be applied on
                ${this.$options.filters.formatDateEnglishShort(this.pendingApprovedEndorsement.effective_date)}.`;
            },
            actionMenuItems() {
                if (this.declaration && this.declaration.hasOwnProperty('endorsements')) {
                    const actionItems = [];

                    actionItems.push({
                        name: `Download ${this.isStandalonePolicy ? 'policy' : 'declaration'} summary`,
                        eventName: 'action-download-summary'
                    });

                    if (this.declaration.is_mrc_downloadable) {
                        actionItems.push({
                            name: 'Download MRC',
                            eventName: 'action-download-mrc'
                        });
                    }

                    if (this.isStandalonePolicy) {
                        actionItems.push({
                            name: `Download policy wording`,
                            eventName: 'action-download-policy-wording'
                        });
                    }

                    if (this.isCaptiveReinsurance) {
                        actionItems.push({
                            name: 'Download original declaration summary',
                            eventName: 'action-download-captive-summary'
                        });
                        actionItems.push({
                            name: 'Download reinsurer declaration summary',
                            eventName: 'action-download-reinsurer-summary'
                        });
                    }
                    if (this.canMakeRepayment) {
                        actionItems.push({
                            name: 'Make repayment',
                            disabled: this.hasActiveEndorsements || this.isOffRisk,
                            disabledTooltip: this.tooltipText('repayment'),
                            eventName: 'action-make-repayment'
                        });
                    }

                    actionItems.push(
                        {
                            name: `Close  ${this.isStandalonePolicy ? 'policy' : 'declaration'}`,
                            disabled: this.hasActiveEndorsements || this.isOffRisk,
                            disabledTooltip: this.tooltipText('close request'),
                            eventName: 'action-close-declaration'
                        }
                    );

                    if (this.isBrokerUser && !this.isCommitment && !this.isInvoice) {
                        actionItems.push(
                            {
                                name: 'Create an MTA',
                                disabled: this.hasActiveEndorsements,
                                disabledTooltip: this.tooltipText('mid-term adjustment'),
                                eventName: 'action-make-mid-term-adjustment'
                            },
                        );
                    }

                    if (!this.isCommitment && !this.isInvoice) {
                        actionItems.push({
                            name: 'Create amendment',
                            disabled: this.hasActiveEndorsements || this.isOffRisk,
                            disabledTooltip: this.tooltipText('amendment'),
                            eventName: 'action-create-amendment'
                        });
                    }

                    return actionItems;
                }
            },
            showEditUmr() {
                return this.$root.user.company.company_type === 'placing_broker' && (!this.declaration.umr || this.editUMR)
            },
            canMakeRepayment() {
                return !this.isCaptiveReinsurance;
            },
            isCaptiveReinsurance() {
                return this.declaration.transaction_type === 'captive_reinsurance';
            },
            hasObligor() {
                return this.declaration.hasOwnProperty('product') && !this.declaration.product.has_no_obligor;
            },
            insurerList: function () {
                return this.declaration.lines.map(line => {
                    if (line.captive_insurer_company_id) {
                        return line.firm_order_offer.captive_insurer_company;
                    }
                    return line.firm_order_offer.insurer_company;
                })
            },
            endorsements: function () {
                return this.declaration.endorsements.filter(e => e.type === 'amendment' || e.type === 'off_risk');
            },
            pendingApprovedEndorsement: function () {
                return this.declaration.endorsements.find(function (endorsement) {
                    return (endorsement.status === 'pending' || endorsement.status === 'approved') &&
                        (endorsement.type === 'repayment' || endorsement.type === 'amendment');
                });
            },
            inProgressEndorsement() {
                return this.declaration.endorsements.find((endorsement) => {
                    return endorsement.type === 'amendment' && endorsement.status === 'in_progress';
                });
            },
            hasActiveRepayment: function () {
                return this.declaration.endorsements.filter(function (endorsement) {
                    return endorsement.type === 'repayment' && endorsement.status === 'approved';
                }).length > 0;
            },
            hasActiveAmendment: function () {
                return this.declaration.endorsements.filter(function (endorsement) {
                    return endorsement.type === 'amendment' && ['pending', 'approved'].includes(endorsement.status);
                }).length > 0;
            },
            hasActiveMidTermAdjustment: function () {
                return this.declaration.endorsements.filter(function (endorsement) {
                    return endorsement.type === 'mid_term_adjustment' && ['pending', 'approved'].includes(endorsement.status);
                }).length > 0;
            },
            hasActiveOffRiskRequest: function () {
                if (this.declaration && this.declaration.hasOwnProperty('endorsements')) {
                    return this.declaration.endorsements.filter(function (endorsement) {
                        return endorsement.type === 'off_risk' && ['pending', 'approved', 'captive_approved'].includes(endorsement.status);
                    }).length > 0;
                }

                return false;
            },
            hasActiveEndorsements: function () {
                return this.declaration.endorsements.filter(e => ['pending', 'approved', 'captive_reviewing', 'captive_approved', 'draft'].includes(e.status)).length > 0;
            },
            isOffRisk: function () {
                return this.declaration.status === 'off_risk';
            },
            versionHistoryRows: function () {
                return this.declarationAudit.filter(audit => ((audit.origin === 'Endorsement') || (audit.event === 'created' && audit.auditable_type === 'App\\Broker\\Models\\Declaration')));
            },
            hasFinalPremium() {
                return Boolean(this.declaration.final_premium);
            },
            hasPendingOffRisk() {
                return this.hasActiveOffRiskRequest && !this.isOffRisk;
            },
            canChangeClientReference() {
                return this.$root.user.company.company_type === 'placing_broker' || this.$root.user.company.company_type === 'client'
            },
            isBrokerUser() {
                return this.$root.user.company.company_type === 'placing_broker' || this.$root.user.company.company_type === 'producing_broker';
            },
            isCommitment() {
                return this.declaration.quote_type?.key === 'commitment';
            },
            isInvoice() {
                return this.declaration.quote_type?.key === 'invoice';
            },
            canCreateInvoice() {
                return this.declaration.available_capacity > 0;
            }
        },
        data() {
            return {
                activeTab: 'product',
                declaration: {currency: {}},
                openDeclarationDetailsPanelIndex: 0,
                openSignedLinesPanelIndex: 0,
                repayments: [],
                declarationAudit: [],
                loading: true,
                currentStep: 1,
                showRepaymentDialog: false,
                showOffRiskDeclarationDialog: false,
                repaymentDate: this.$moment().format("YYYY-MM-DD"),
                repaymentDateInput: false,
                finalPremium: '',
                repaymentAmount: '',
                offRiskDate: null,
                offRiskDateInput: false,
                settlementDueDate: null,
                settlementDueDateInput: false,
                repaymentValid: true,
                offRiskValid: true,
                umr: null,
                editUMR: false,
                errorMessage: '',
                offRiskLoading: false,
                repaymentLoading: false,
                openAmendmentPanelIndex: null,
                openSubAgreementPanelIndex: null,
                openRepaymentPanelIndex: null,
                openPremiumPanelIndex: null,
                midTermAdjustment: [],
                mtaLoading: false,
                inProgressEndorsementText: 'An amendment is in progress.  Please attach Policy Wording and Market Reform Contract documentation before submitting to insurers.',
                repayment: {}
            }
        },
        watch: {
            actionMenuItems: {
                handler(newItems) {
                    this.$eventBus.$emit('update-action-menu-items', newItems);
                },
                immediate: true
            },
            hasPendingOffRisk: {
                handler(hasActiveOffRisk) {
                    this.$eventBus.$emit('update-has-active-off-risk', hasActiveOffRisk);
                },
                immediate: true
            },
            offRiskDate(newOffRiskDate) {
                if (this.settlementDueDate === null) {
                    this.settlementDueDate = newOffRiskDate
                        ? this.$moment(newOffRiskDate).add(60, 'days').format('YYYY-MM-DD')
                        : null;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
h3 {
    font-size: rem(24px);
}

.summary-field-update-row {
    display: flex;
    padding-left: 40%;

    .x-btn {
        flex-grow: 0 !important;
        margin-top: 20px;
    }
}
</style>
